import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Layout from './components/Layout';
import SupportChatWidget from './components/SupportChatWidget';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import SecuritySettings from './pages/SecuritySettings';
import Dashboard from './pages/Dashboard';
import Meditations from './pages/Meditations';
import MeditationPlayer from './pages/MeditationPlayer';
import Timer from './pages/Timer';
import Breathing from './pages/Breathing';
import Affirmations from './pages/Affirmations';
import Journal from './pages/Journal';
import Articles from './pages/Articles';
import HealingSessions from './pages/HealingSessions';
import Profile from './pages/Profile';
import Admin from './pages/Admin';
import Community from './pages/Community';
import Shop from './pages/Shop';
import Contact from './pages/Contact';
import PrivacyPolicy from './pages/PrivacyPolicy';
import DataDeletion from './pages/DataDeletion';
import AdminUsers from './pages/admin/AdminUsers';
import AdminThemeSettings from './pages/admin/AdminThemeSettings';
import AdminContent from './pages/admin/AdminContent';
import AdminPractitioners from './pages/admin/AdminPractitioners';
import ManageCommunity from './pages/admin/ManageCommunity';
import MarketingTools from './pages/admin/MarketingTools';
import SocialMediaManager from './pages/admin/SocialMediaManager';
import SocialCallback from './pages/SocialCallback';
import ManageProducts from './pages/admin/ManageProducts';
import SalesDashboard from './pages/admin/SalesDashboard';
import AdminSupport from './pages/admin/AdminSupport';
import PractitionerDashboard from './pages/practitioner/PractitionerDashboard';
import './App.css';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen cosmic-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cosmic-gold/30 border-t-cosmic-gold rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-cosmic-goldLight font-cinzel">Entering The Aura Space...</p>
        </div>
      </div>
    );
  }

  return user ? <Layout>{children}</Layout> : <Navigate to="/login" />;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen cosmic-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cosmic-gold/30 border-t-cosmic-gold rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-cosmic-goldLight font-cinzel">Loading...</p>
        </div>
      </div>
    );
  }

  return user ? <Navigate to="/dashboard" /> : children;
}

function AdminRoute({ children, allowedRoles }) {
  const { user, loading, hasRole } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen cosmic-bg flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-cosmic-gold/30 border-t-cosmic-gold rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" />;
  
  if (!hasRole(allowedRoles)) {
    return <Navigate to="/dashboard" />;
  }

  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/data-deletion" element={<DataDeletion />} />
      <Route path="/social/callback/:platform" element={<PrivateRoute><SocialCallback /></PrivateRoute>} />
      <Route path="/" element={<Navigate to="/dashboard" />} />
      
      <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
      <Route path="/meditations" element={<PrivateRoute><Meditations /></PrivateRoute>} />
      <Route path="/meditations/:id" element={<PrivateRoute><MeditationPlayer /></PrivateRoute>} />
      <Route path="/timer" element={<PrivateRoute><Timer /></PrivateRoute>} />
      <Route path="/breathing" element={<PrivateRoute><Breathing /></PrivateRoute>} />
      <Route path="/affirmations" element={<PrivateRoute><Affirmations /></PrivateRoute>} />
      <Route path="/journal" element={<PrivateRoute><Journal /></PrivateRoute>} />
      <Route path="/articles" element={<PrivateRoute><Articles /></PrivateRoute>} />
      <Route path="/articles/:id" element={<PrivateRoute><Articles /></PrivateRoute>} />
      <Route path="/healing-sessions" element={<PrivateRoute><HealingSessions /></PrivateRoute>} />
      <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
      <Route path="/security" element={<PrivateRoute><SecuritySettings /></PrivateRoute>} />
      <Route path="/community" element={<PrivateRoute><Community /></PrivateRoute>} />
      <Route path="/shop" element={<PrivateRoute><Shop /></PrivateRoute>} />
      <Route path="/contact" element={<PrivateRoute><Contact /></PrivateRoute>} />
      
      <Route path="/admin" element={
        <AdminRoute allowedRoles={['super_admin', 'user_admin', 'content_admin', 'practitioner_admin']}>
          <Admin />
        </AdminRoute>
      } />
      <Route path="/admin/users" element={
        <AdminRoute allowedRoles={['super_admin', 'user_admin']}>
          <AdminUsers />
        </AdminRoute>
      } />
      <Route path="/admin/theme" element={
        <AdminRoute allowedRoles={['super_admin', 'content_admin']}>
          <AdminThemeSettings />
        </AdminRoute>
      } />
      <Route path="/admin/content" element={
        <AdminRoute allowedRoles={['super_admin', 'content_admin']}>
          <AdminContent />
        </AdminRoute>
      } />
      <Route path="/admin/practitioners" element={
        <AdminRoute allowedRoles={['super_admin', 'practitioner_admin']}>
          <AdminPractitioners />
        </AdminRoute>
      } />
      <Route path="/admin/community" element={
        <AdminRoute allowedRoles={['super_admin', 'content_admin']}>
          <ManageCommunity />
        </AdminRoute>
      } />
      <Route path="/admin/marketing" element={
        <AdminRoute allowedRoles={['super_admin', 'content_admin', 'practitioner_admin', 'practitioner']}>
          <MarketingTools />
        </AdminRoute>
      } />
      <Route path="/admin/social" element={
        <AdminRoute allowedRoles={['super_admin', 'content_admin', 'practitioner_admin', 'practitioner']}>
          <SocialMediaManager />
        </AdminRoute>
      } />
      <Route path="/admin/products" element={
        <AdminRoute allowedRoles={['super_admin', 'content_admin']}>
          <ManageProducts />
        </AdminRoute>
      } />
      <Route path="/admin/sales" element={
        <AdminRoute allowedRoles={['super_admin', 'content_admin']}>
          <SalesDashboard />
        </AdminRoute>
      } />
      <Route path="/admin/support" element={
        <AdminRoute allowedRoles={['super_admin', 'user_admin', 'content_admin']}>
          <AdminSupport />
        </AdminRoute>
      } />
      <Route path="/practitioner/dashboard" element={
        <AdminRoute allowedRoles={['super_admin', 'practitioner_admin', 'practitioner']}>
          <PractitionerDashboard />
        </AdminRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <SupportChatWidget />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
// Build: 1771687665
// Build: 1771690010
